/**
 * Sommaire et temps de lecture d'un contenu redige en Markdown.
 * Table of contents and reading time of Markdown learning content.
 */

/** Normalise un titre en identifiant d'ancre stable. | Stable anchor id. */
export function slugify(title: string): string {
  const slug = title
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'section';
}

export interface OutlineEntry {
  id: string;
  title: string;
  /** 2 pour un titre de section, 3 pour un sous-titre. */
  level: number;
}

/**
 * Titres de niveau 2 et 3, dans l'ordre du document. Les blocs de code sont
 * ignores : un `#` y designe un commentaire, pas un titre.
 */
export function tableOfContents(markdown: string): OutlineEntry[] {
  const entries: OutlineEntry[] = [];
  const seen = new Map<string, number>();
  let insideFence = false;

  for (const line of markdown.split('\n')) {
    if (/^\s*(```|~~~)/.test(line)) {
      insideFence = !insideFence;
      continue;
    }
    if (insideFence) continue;

    const found = /^(#{2,3}) +(.+?)\s*#*$/.exec(line);
    if (!found) continue;

    const title = (found[2] ?? '').replace(/[*_`]/g, '').trim();
    if (!title) continue;

    // Deux sections homonymes ne peuvent pas partager la meme ancre.
    const base = slugify(title);
    const occurrence = (seen.get(base) ?? 0) + 1;
    seen.set(base, occurrence);

    entries.push({ id: occurrence === 1 ? base : `${base}-${occurrence}`, title, level: (found[1] ?? '').length });
  }

  return entries;
}

/** Temps de lecture estime, a 200 mots par minute. | Estimated reading time. */
export function readingMinutes(markdown: string): number {
  const words = markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .split(/\s+/)
    .filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}
